import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader, Swords, Skull, Shield, X, ArrowRight, Crosshair, Flame } from "lucide-react";

/**
 * PvP Attack flow modal.
 *
 * Props:
 *   target        { user_id, callsign, horde, rank_name, rank_index, is_rival }
 *   onClose()     dismiss
 *   onResolved()  fired after successful launch (parent should refresh state/leaderboard)
 */
export default function AttackModal({ target, onClose, onResolved }) {
  const [status, setStatus] = useState(null);
  const [tactic, setTactic] = useState("overwhelm");
  const [wagerEnabled, setWagerEnabled] = useState(false);
  const [wagerPct, setWagerPct] = useState(0.25);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [result, setResult] = useState(null);

  // Load /attacks/status once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/attacks/status");
        if (!cancelled) {
          setStatus(data);
          // Default the slider to the lower bound the backend permits
          if (data?.wager?.pct_min) setWagerPct(data.wager.pct_min);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load attack status.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-run preview when tactic OR wager changes
  useEffect(() => {
    if (!target?.user_id || result) return;
    let cancelled = false;
    setLoadingPreview(true);
    (async () => {
      try {
        const { data } = await api.post("/attacks/preview", {
          target_user_id: target.user_id,
          tactic,
          wager_pct: wagerEnabled ? wagerPct : 0,
        });
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          toast.error(e?.response?.data?.detail || "Preview unavailable.");
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [target?.user_id, tactic, wagerEnabled, wagerPct, result]);

  const launch = async () => {
    setLaunching(true);
    try {
      const { data } = await api.post("/attacks/launch", {
        target_user_id: target.user_id,
        tactic,
        wager_pct: wagerEnabled ? wagerPct : 0,
      });
      setResult(data);
      onResolved?.();
      if (data.win) toast.success(`VICTORY · +${data.horde_transfer} HORDE TAKEN`);
      else toast.error(`REPELLED · -${data.attacker_losses} FALLEN`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attack failed.");
    } finally {
      setLaunching(false);
    }
  };

  if (!target) return null;

  const tactics = status?.tactics || [];
  const force = status?.force_committed ?? 0;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
         onClick={onClose} data-testid="attack-modal-overlay">
      <div className="bg-[#0a0303] border border-blood/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()} data-testid="attack-modal">
        {/* header */}
        <div className="flex items-center justify-between border-b border-blood/30 px-6 py-4">
          <div className="flex items-center gap-3">
            <Crosshair className="w-4 h-4 text-blood" />
            <div>
              <p className="tac-label text-blood">PVP STRIKE</p>
              <p className="font-serif-h text-2xl text-bone leading-tight">
                {target.callsign}
                <span className="text-muted-h text-sm ml-2 font-mono tracking-widest">
                  {(target.rank_name || "").toUpperCase()} · {target.horde?.toLocaleString()} HORDE
                </span>
                {target.revenge_active && (
                  <span className="ml-2 px-1.5 py-0.5 border border-blood text-blood text-[0.55rem] font-mono tracking-widest align-middle"
                        data-testid="attack-revenge-pill">
                    × REVENGE
                  </span>
                )}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-h hover:text-bone p-1"
                  data-testid="attack-modal-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!result ? (
          <div className="p-6 space-y-5">
            {/* revenge banner */}
            {target.revenge_active && (
              <div className="border border-blood bg-[#1a0606] p-3 flex items-start gap-2"
                   data-testid="attack-revenge-banner">
                <Flame className="w-4 h-4 text-blood mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-mono text-[0.65rem] tracking-[0.2em] text-blood">
                    REVENGE STRIKE READY{status?.blood_moon?.active ? " · BLOOD MOON" : ""}
                  </p>
                  <p className="text-xs text-bone mt-1 leading-snug">
                    {target.callsign} attacked you first. Stake transfer is{" "}
                    <span className="text-blood">
                      ×{status?.blood_moon?.active ? "3" : "2"}
                    </span>
                    . A win banks an extra <span className="text-[#7FBA9D]">+0.5%</span> rep on top of the swing.
                  </p>
                </div>
              </div>
            )}

            {/* Blood Moon notice for non-revenge attacks */}
            {!target.revenge_active && status?.blood_moon?.active && (
              <div className="border border-blood/60 bg-[#0e0202] p-2.5 flex items-center gap-2"
                   data-testid="attack-blood-moon-notice">
                <Flame className="w-3.5 h-3.5 text-blood flex-shrink-0" />
                <p className="text-[0.7rem] text-bone leading-snug">
                  <span className="font-mono tracking-[0.2em] text-blood">BLOOD MOON</span> · Your strike will leave only a <span className="text-blood">12h</span> shield, not 24h.
                </p>
              </div>
            )}

            {/* commit bar */}
            {status && (
              <div className="border-l-2 border-blood/60 pl-4" data-testid="attack-commit-info">
                <p className="tac-label">COMMIT // 10% OF YOUR HORDE</p>
                <p className="text-bone font-serif-h text-3xl mt-1 tabular-nums">
                  {force}
                  <span className="text-muted-h text-sm ml-2 font-mono">
                    OF {status.personal_horde?.toLocaleString()}
                  </span>
                </p>
                <p className="text-[0.65rem] text-muted-h mt-1">
                  Forced commitment. The dead you take with you may not return. Stake = 5% of the loser's surviving horde.
                </p>
              </div>
            )}

            {/* tactic picker */}
            <div data-testid="attack-tactic-picker">
              <p className="tac-label mb-2">CHOOSE A TACTIC</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {tactics.map((t) => {
                  const active = tactic === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setTactic(t.key)}
                      className={`text-left p-3 border transition-all ${
                        active ? "border-blood bg-[#1a0606]"
                               : "border-[var(--border-subtle)] hover:border-blood/60"
                      }`}
                      data-testid={`tactic-${t.key}`}
                    >
                      <p className="font-mono text-[0.65rem] tracking-[0.2em] text-blood mb-1">
                        {t.label.toUpperCase()}
                      </p>
                      <p className="text-xs text-bone leading-snug">{t.blurb}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* reputation wager (Lieutenant+) */}
            {status?.wager?.unlocked ? (
              <WagerControl
                rep={status.reputation || 0}
                pctMin={status.wager.pct_min}
                pctMax={status.wager.pct_max}
                enabled={wagerEnabled}
                pct={wagerPct}
                onToggle={() => setWagerEnabled((v) => !v)}
                onPctChange={setWagerPct}
              />
            ) : status?.wager ? (
              <div className="border border-dashed border-[var(--border-subtle)] p-3"
                   data-testid="attack-wager-locked">
                <p className="font-mono text-[0.6rem] tracking-[0.2em] text-muted-h flex items-center gap-2">
                  <Flame className="w-3 h-3" />
                  REP WAGER · LOCKED
                </p>
                <p className="text-[0.7rem] text-muted-h mt-1 leading-relaxed">
                  Reach <span className="text-bone">{(status.wager.min_rank_name || "").toUpperCase()}</span> to risk reputation alongside the strike for higher stake transfers and bigger rep swings.
                </p>
              </div>
            ) : null}

            {/* prediction */}
            <div className="border-t border-[var(--border-subtle)] pt-4" data-testid="attack-preview">
              <p className="tac-label mb-2">DETERMINISTIC FORECAST</p>
              {loadingPreview ? (
                <p className="text-muted-h text-xs flex items-center gap-2">
                  <Loader className="w-3 h-3 animate-spin" /> Calculating...
                </p>
              ) : preview ? (
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-[0.65rem] text-muted-h tracking-widest">
                      OUTCOME
                    </span>
                    <span className={`font-serif-h text-xl ${
                      preview.win ? "text-[#7FBA9D]" : "text-blood"
                    }`} data-testid="attack-preview-outcome">
                      {preview.win ? "VICTORY" : "REPELLED"}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center font-mono">
                    <div className="border border-[var(--border-subtle)] p-2">
                      <p className="text-[0.55rem] text-muted-h tracking-widest">YOU LOSE</p>
                      <p className="text-blood text-lg tabular-nums">{preview.attacker_losses}</p>
                    </div>
                    <div className="border border-[var(--border-subtle)] p-2">
                      <p className="text-[0.55rem] text-muted-h tracking-widest">THEY LOSE</p>
                      <p className="text-bone text-lg tabular-nums">{preview.defender_losses}</p>
                    </div>
                    <div className="border border-[var(--border-subtle)] p-2">
                      <p className="text-[0.55rem] text-muted-h tracking-widest">
                        {preview.win ? "YOU TAKE" : "TARGET TAKES"}
                      </p>
                      <p className={`text-lg tabular-nums ${
                        preview.win ? "text-[#7FBA9D]" : "text-blood"
                      }`}>
                        +{preview.horde_transfer}
                      </p>
                    </div>
                  </div>
                  <p className="text-[0.65rem] text-muted-h leading-relaxed">
                    Defender posture: <span className="text-bone">{preview.defender_posture_label}</span>.
                    {preview.counter_bonus_attacker > 0 && " You counter their stance — +15% offense."}
                    {preview.counter_bonus_attacker < 0 && " They are positioned against this exact tactic — -15% offense."}
                  </p>
                </div>
              ) : (
                <p className="text-muted-h text-xs italic">No forecast.</p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={onClose} className="btn-ghost"
                      data-testid="attack-cancel">CANCEL</button>
              <button
                onClick={launch}
                disabled={launching || !preview}
                className="btn-blood flex items-center gap-2"
                data-testid="attack-confirm"
              >
                {launching ? <Loader className="w-3 h-3 animate-spin" /> : <Swords className="w-3 h-3" />}
                STRIKE
              </button>
            </div>
          </div>
        ) : (
          <ResultPane result={result} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function ResultPane({ result, onClose }) {
  const win = !!result.win;
  return (
    <div className="p-6 space-y-5" data-testid="attack-result">
      <div className={`text-center py-6 border ${
        win ? "border-[#7FBA9D]/40" : "border-blood/50"
      }`}>
        <p className={`font-serif-h text-4xl mb-2 ${win ? "text-[#7FBA9D]" : "text-blood"}`}
           data-testid="attack-result-headline">
          {win ? "VICTORY" : "DEFEAT"}
        </p>
        <p className="font-mono text-xs text-muted-h tracking-[0.2em]">
          {(result.tactic_label || "").toUpperCase()} VS {(result.defender_posture_label || "").toUpperCase()}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 font-mono">
        <div className="border-l-2 border-blood/50 pl-3 py-2">
          <p className="text-[0.6rem] text-muted-h tracking-widest">YOUR LOSSES</p>
          <p className="text-blood text-2xl tabular-nums flex items-center gap-2">
            <Skull className="w-3 h-3" /> {result.attacker_losses}
          </p>
        </div>
        <div className="border-l-2 border-bone/30 pl-3 py-2">
          <p className="text-[0.6rem] text-muted-h tracking-widest">THEIR LOSSES</p>
          <p className="text-bone text-2xl tabular-nums">{result.defender_losses}</p>
        </div>
        <div className="border-l-2 border-[#7FBA9D]/40 pl-3 py-2">
          <p className="text-[0.6rem] text-muted-h tracking-widest">
            {win ? "STAKE WON" : "STAKE LOST"}
          </p>
          <p className={`text-2xl tabular-nums ${win ? "text-[#7FBA9D]" : "text-blood"}`}>
            {win ? "+" : "-"}{result.horde_transfer}
          </p>
        </div>
        <div className="border-l-2 border-[var(--border-subtle)] pl-3 py-2">
          <p className="text-[0.6rem] text-muted-h tracking-widest">YOUR HORDE NOW</p>
          <p className="text-bone text-2xl tabular-nums">{result.new_attacker_horde?.toLocaleString()}</p>
        </div>
      </div>

      <div className="border-t border-[var(--border-subtle)] pt-3 flex items-start gap-2 text-xs text-muted-h">
        <Shield className="w-3 h-3 mt-0.5 text-bone/70 flex-shrink-0" />
        <p className="leading-relaxed">
          {result.target_callsign} now holds a <span className="text-bone">24-hour shield</span>. Find another mark.
        </p>
      </div>

      {result.revenge_active && (
        <div className="border-t border-[var(--border-subtle)] pt-3 flex items-start gap-2 text-xs"
             data-testid="attack-result-revenge">
          <Flame className="w-3 h-3 mt-0.5 text-blood flex-shrink-0" />
          <p className="leading-relaxed text-muted-h">
            Revenge token <span className="text-bone">spent</span> · stake doubled
            {result.rep_revenge_bonus > 0 && (
              <> · counter-punch rep <span className="text-[#7FBA9D]">+{(result.rep_revenge_bonus * 100).toFixed(2)}%</span></>
            )}
          </p>
        </div>
      )}

      {result.wager_pct > 0 && (
        <div className="border-t border-[var(--border-subtle)] pt-3 flex items-start gap-2 text-xs"
             data-testid="attack-result-wager">
          <Flame className="w-3 h-3 mt-0.5 text-blood flex-shrink-0" />
          <p className="leading-relaxed text-muted-h">
            Reputation wager <span className="text-bone">{Math.round(result.wager_pct * 100)}%</span> ·
            {" "}
            <span className={result.rep_change >= 0 ? "text-[#7FBA9D]" : "text-blood"}>
              {result.rep_change >= 0 ? "+" : ""}{(result.rep_change * 100).toFixed(2)}% rep
            </span>
            {" · "}new rep <span className="text-bone">{(result.new_attacker_reputation * 100).toFixed(2)}%</span>
          </p>
        </div>
      )}

      <button onClick={onClose} className="btn-blood w-full flex items-center justify-center gap-2"
              data-testid="attack-result-close">
        WALK AWAY <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

function WagerControl({ rep, pctMin, pctMax, enabled, pct, onToggle, onPctChange }) {
  const repPct = (rep || 0) * 100;
  const onTable = (rep || 0) * pct;
  const safeMin = pctMin ?? 0.10;
  const safeMax = pctMax ?? 0.50;
  return (
    <div
      className={`border p-3 transition-colors ${
        enabled ? "border-blood bg-[#1a0606]" : "border-[var(--border-subtle)]"
      }`}
      data-testid="attack-wager-control"
    >
      <button
        onClick={onToggle}
        type="button"
        className="flex items-center justify-between w-full"
        data-testid="attack-wager-toggle"
      >
        <span className="font-mono text-[0.65rem] tracking-[0.2em] text-blood flex items-center gap-2">
          <Flame className="w-3 h-3" />
          REP WAGER · {enabled ? "ON" : "OFF"}
        </span>
        <span className="font-mono text-[0.6rem] text-muted-h tracking-widest">
          REP {repPct.toFixed(2)}%
        </span>
      </button>

      {enabled && (
        <div className="mt-3 space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="font-mono text-[0.6rem] text-muted-h tracking-widest">
              ON THE TABLE
            </span>
            <span className="font-serif-h text-2xl text-bone tabular-nums">
              {Math.round(pct * 100)}%
              <span className="text-muted-h text-xs ml-2 font-mono">
                = {(onTable * 100).toFixed(2)}% rep
              </span>
            </span>
          </div>
          <input
            type="range"
            min={Math.round(safeMin * 100)}
            max={Math.round(safeMax * 100)}
            step={5}
            value={Math.round(pct * 100)}
            onChange={(e) => onPctChange(Number(e.target.value) / 100)}
            className="w-full accent-[var(--blood)]"
            data-testid="attack-wager-slider"
          />
          <p className="text-[0.65rem] text-muted-h leading-relaxed">
            On <span className="text-[#7FBA9D]">VICTORY</span>: stake transfer scaled ×{(1 + pct).toFixed(2)}, rep gain boosted by the wagered amount.
            On <span className="text-blood">DEFEAT</span>: lose the wagered rep on top of normal losses.
          </p>
        </div>
      )}
      {!enabled && (
        <p className="text-[0.65rem] text-muted-h mt-2 leading-relaxed">
          Risk a slice of your reputation for a bigger stake transfer and a sharper rep swing.
        </p>
      )}
    </div>
  );
}
